import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboardReport, getUnvisitedCustomers, getUsers, getRepDetails, getTargets } from '@/api/api';
import { Users, ClipboardList, ShoppingCart, AlertCircle, Bell, TrendingUp, Download, Package, Phone, Target, UserPlus, Activity, Zap, DollarSign, Award, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import TLIcon from '@/components/TLIcon';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import TargetAchievementCard from '@/components/TargetAchievementCard';
import { TeamRanking } from '@/components/TeamRanking';
import { PrimHistoryChart } from '@/components/PrimHistoryChart';
import { AchievementBar } from '@/components/AchievementBar';

const fmtPoints = (v) => new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(v || 0);
import { getLocalDateString } from '@/utils/dateUtils';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [unvisitedCustomers, setUnvisitedCustomers] = useState([]);
  const [noContactCustomers, setNoContactCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [showAllRemindersDialog, setShowAllRemindersDialog] = useState(false);
  const [showNoContactDialog, setShowNoContactDialog] = useState(false);
  const [selectedRepFilter, setSelectedRepFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [targets, setTargets] = useState([]);
  const [showRepDetailsDialog, setShowRepDetailsDialog] = useState(false);
  const [repDetailsData, setRepDetailsData] = useState({ type: '', rep: '', data: [] });
  const [targetAchievement, setTargetAchievement] = useState(null);
  
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const firstDayOfMonth = new Date(year, month, 1);
  
  const [startDate, setStartDate] = useState(getLocalDateString(firstDayOfMonth));
  const [endDate, setEndDate] = useState(getLocalDateString(today));

  useEffect(() => {
    if (user) {
      loadData();
      loadTargets();
      loadUsers();
      loadTargetAchievement();
      loadNoContactCustomers();
      requestNotificationPermission();
    }
  }, [user]);

  useEffect(() => {
    if (user && data !== null) {
      loadData();
    }
  }, [selectedRepFilter, startDate, endDate]);

  const loadUsers = async () => {
    try {
      const usersData = await getUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi');
    }
  };

  const loadTargets = async () => {
    try {
      const targetsData = await getTargets();
      setTargets(targetsData);
    } catch (error) {
      console.error('Targets loading failed');
    }
  };

  const loadData = async () => {
    try {
      let representativeId = null;
      if (user?.role === 'admin') {
        representativeId = selectedRepFilter === 'all' ? null : selectedRepFilter;
      } else {
        representativeId = user?.username;
      }
      
      const [report, unvisited] = await Promise.all([
        getDashboardReport(representativeId, startDate, endDate),
        getUnvisitedCustomers()
      ]);
      setData(report);
      setUnvisitedCustomers(unvisited);
      
      if (unvisited.length > 0 && Notification.permission === 'granted') {
        showNotification(unvisited);
      }
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadTargetAchievement = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const now = new Date();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();
      
      const response = await axios.get(
        `${API_BASE}/api/target-achievements?token=${token}&month=${month}&year=${year}`
      );
      
      if (user?.role !== 'admin') {
        const myData = response.data.find(item => item.username === user?.username);
        setTargetAchievement(myData || null);
      } else {
        setTargetAchievement(response.data[0] || null);
      }
    } catch (error) {
      console.error('Target achievement error:', error);
    }
  };

  // 3 Aydır Temas Edilmeyen Müşterileri Yükle
  const [noContactTotalCount, setNoContactTotalCount] = useState(0);
  const [noContactRepCounts, setNoContactRepCounts] = useState({});
  
  const loadNoContactCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      const response = await axios.get(
        `${API_BASE}/api/reports/no-contact-3months?token=${token}&limit=100`
      );
      
      // Yeni API formatı: { total_count, rep_counts, customers }
      if (response.data.customers) {
        setNoContactCustomers(response.data.customers);
        setNoContactTotalCount(response.data.total_count || 0);
        setNoContactRepCounts(response.data.rep_counts || {});
      } else {
        // Eski format için geriye uyumluluk
        setNoContactCustomers(response.data);
        setNoContactTotalCount(response.data.length);
      }
    } catch (error) {
      console.error('No contact customers error:', error);
    }
  };

  // 3 Aydır Temas Edilmeyen Müşterileri Excel'e Aktar
  const exportNoContactToExcel = async () => {
    const loadingToast = toast.loading('Tüm müşteriler yükleniyor... Bu işlem birkaç dakika sürebilir.');
    
    try {
      // TÜM müşterileri çek (limit olmadan)
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      const response = await axios.get(
        `${API_BASE}/api/reports/no-contact-3months?token=${token}&limit=10000`,
        { timeout: 300000 } // 5 dakika timeout
      );
      
      toast.dismiss(loadingToast);
      
      const allCustomers = response.data.customers || response.data;
      
      if (!allCustomers || allCustomers.length === 0) {
        toast.error('Dışa aktarılacak müşteri bulunamadı');
        return;
      }

      // CSV header
      const headers = ['Müşteri', 'Temsilci', 'Telefon', 'Araç Adedi', 'Son Temas Tarihi', 'Temas Türü', 'Geçen Gün'];
      
      // CSV rows
      const rows = allCustomers.map(customer => [
        customer.customer_name || '',
        customer.representative || 'Atanmamış',
        customer.phone || '-',
        customer.device_count || 0,
        customer.last_contact_date 
          ? format(new Date(customer.last_contact_date), 'dd.MM.yyyy', { locale: tr })
          : 'Hiç temas yok',
        customer.last_contact_type || '-',
        customer.days_since_contact !== null ? `${customer.days_since_contact} gün` : '∞'
      ]);

      // BOM for Excel UTF-8 support
      const BOM = '\uFEFF';
      const csvContent = BOM + [
        headers.join(';'),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
      ].join('\n');

      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `3_Aydir_Temas_Edilmeyen_Musteriler_${format(new Date(), 'dd-MM-yyyy')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`${allCustomers.length} müşteri Excel'e aktarıldı`);
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Excel export error:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('İşlem zaman aşımına uğradı. Lütfen tekrar deneyin.');
      } else {
        toast.error('Excel aktarımı sırasında hata oluştu');
      }
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
    }
  };

  const showNotification = (customers) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification('ProGo Mobil Sistemler Hatırlatma', {
        body: `${customers.length} müşterinin yenileme süresi doldu veya dolmak üzere!`,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: 'renewal-reminder',
        requireInteraction: true
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  };

  const handleRepPerformanceClick = async (rep, type) => {
    try {
      // Dashboard tablosu ile aynı tarih aralığını kullan (startDate, endDate state'leri)
      const details = await getRepDetails(rep, type, startDate, endDate);
      
      setRepDetailsData({
        type,
        rep,
        data: details
      });
      setShowRepDetailsDialog(true);
    } catch (error) {
      console.error('Rep details error:', error);
      toast.error('Detaylar yüklenirken hata oluştu');
    }
  };

  const exportRepPerformanceToExcel = () => {
    if (!data || !data.rep_visits_tm) {
      toast.error('Export edilecek veri bulunamadı');
      return;
    }

    const allReps = new Set([
      ...Object.keys(data.rep_visits_tm || {}),
      ...Object.keys(data.rep_calls_tm || {}),
      ...Object.keys(data.rep_sales_tm || {}),
      ...Object.keys(data.rep_cameras_tm || {}),
      ...Object.keys(data.rep_sensors_tm || {}),
      ...Object.keys(data.rep_m2m_tm || {})
    ]);

    let csv = 'Temsilci,Ziyaret Sayısı,Arama Sayısı,Satılan Arvento,Satılan Kamera,Satılan Sensör,Satılan M2M\n';
    
    allReps.forEach(rep => {
      const visits = data.rep_visits_tm[rep] || 0;
      const calls = data.rep_calls_tm?.[rep] || 0;
      const arvento = data.rep_sales_tm[rep] || 0;
      const camera = data.rep_cameras_tm[rep] || 0;
      const sensor = data.rep_sensors_tm?.[rep] || 0;
      const m2m = data.rep_m2m_tm?.[rep] || 0;
      
      csv += `${rep},${visits},${calls},${arvento},${camera},${sensor},${m2m}\n`;
    });

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `temsilci_performans_${getLocalDateString()}.csv`;
    link.click();
    
    toast.success('Rapor başarıyla indirildi');
  };

  if (loading) {
    return <div className="text-center py-12">Yükleniyor...</div>;
  }

  if (!data) {
    return <div className="text-center py-12">Veri yüklenemedi</div>;
  }

  const repData = {
    visits: data.rep_visits_tm || {},
    calls: data.rep_calls_tm || {},
    sales: data.rep_sales_tm || {},
    cameras: data.rep_cameras_tm || {},
    sensors: data.rep_sensors_tm || {},
    m2m: data.rep_m2m_tm || {},
    points: data.rep_profit_tm || {}
  };
  const hasRepData = ['visits', 'calls', 'sales', 'cameras', 'sensors', 'm2m', 'points'].some(k => Object.keys(repData[k]).length > 0);

  // Modern Widget Kartları
  const statCards = [
    {
      title: 'Toplam Müşteri',
      value: data.total_customers,
      icon: Users,
      gradient: 'from-blue-500 to-blue-600',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      testId: 'stat-total-customers'
    },
    {
      title: 'Toplam Cihaz',
      value: data.total_device_count ?? 0,
      icon: Package,
      gradient: 'from-indigo-500 to-indigo-600',
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      testId: 'stat-total-device-count',
      subtitle: user?.role === 'admin' ? 'Tüm cihazlar' : 'Portföyünüz'
    },
    {
      title: 'Aktif Cihaz',
      value: data.total_active_device_count ?? 0,
      icon: Zap,
      gradient: 'from-green-500 to-emerald-600',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      testId: 'stat-active-device-count',
      subtitle: 'Çalışan cihazlar'
    },
    {
      title: 'Pasif Cihaz',
      value: data.total_inactive_device_count ?? 0,
      icon: Activity,
      gradient: 'from-red-500 to-red-600',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      testId: 'stat-total-inactive-device-count',
      subtitle: 'Devre dışı'
    },
    {
      title: 'Aktif Müşteri',
      value: data.active_customers_count ?? 0,
      icon: Users,
      gradient: 'from-teal-500 to-teal-600',
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      testId: 'stat-active-customers',
      subtitle: 'Cihazı olan'
    },
    {
      title: 'Pasif Müşteri',
      value: data.passive_customers_count ?? 0,
      icon: Users,
      gradient: 'from-gray-500 to-gray-600',
      iconBg: 'bg-gray-100',
      iconColor: 'text-gray-600',
      testId: 'stat-passive-customers',
      subtitle: 'Cihazı olmayan'
    },
    {
      title: 'Yeni Müşteri',
      value: data.new_customers_filtered ?? 0,
      icon: UserPlus,
      gradient: 'from-cyan-500 to-cyan-600',
      iconBg: 'bg-cyan-100',
      iconColor: 'text-cyan-600',
      testId: 'stat-new-customers'
    },
    {
      title: 'Ziyaret',
      value: data.visits_filtered ?? 0,
      icon: ClipboardList,
      gradient: 'from-green-500 to-green-600',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      testId: 'stat-visits'
    },
    {
      title: 'Arama',
      value: data.calls_filtered ?? 0,
      icon: Phone,
      gradient: 'from-purple-500 to-purple-600',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      testId: 'stat-calls'
    },
    {
      title: 'Arvento Satış',
      value: data.total_sales_quantity ?? 0,
      icon: ShoppingCart,
      gradient: 'from-orange-500 to-orange-600',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      testId: 'stat-total-sales',
      subtitle: 'Bu ay'
    }
  ];

  if (user?.role === 'admin') {
    statCards.push({
      title: 'Abonelik Ort.',
      value: (data.daily_avg_calls || 0).toFixed(1),
      subtitle: 'Günlük arama',
      icon: Phone,
      gradient: 'from-purple-500 to-purple-600',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      testId: 'stat-abonelik-avg-calls'
    });
  }

  const myPerformanceCards = [
    {
      title: 'Hedef Gerçekleştirme',
      value: `%${(data.target_realization || 0).toFixed(1)}`,
      subtitle: data.target_amount > 0 ? `Hedef: ${data.target_amount.toLocaleString('tr-TR')} ₺` : 'Hedef yok',
      icon: Target,
      gradient: data.target_realization >= 100 ? 'from-green-500 to-emerald-600' : data.target_realization >= 75 ? 'from-yellow-500 to-yellow-600' : 'from-red-500 to-red-600',
      iconBg: data.target_realization >= 100 ? 'bg-green-100' : data.target_realization >= 75 ? 'bg-yellow-100' : 'bg-red-100',
      iconColor: data.target_realization >= 100 ? 'text-green-600' : data.target_realization >= 75 ? 'text-yellow-600' : 'text-red-600',
      testId: 'stat-target-realization'
    },
    {
      title: user?.role === 'admin' ? 'Ziyaret Ort.' : user?.role === 'abonelik' ? 'Arama Ort.' : 'Ziyaret Ort.',
      value: user?.role === 'admin'
        ? (data.temsilci_avg_visits || 0).toFixed(1)
        : user?.role === 'abonelik' 
          ? (data.daily_avg_calls || 0).toFixed(1) 
          : (data.daily_avg_visits || 0).toFixed(1),
      subtitle: user?.role === 'admin'
        ? 'Temsilci ortalama'
        : user?.role === 'abonelik' 
          ? 'Günlük ortalama' 
          : 'Günlük ortalama',
      icon: user?.role === 'abonelik' ? Phone : ClipboardList,
      gradient: 'from-blue-500 to-blue-600',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      testId: user?.role === 'abonelik' ? 'stat-daily-avg-calls' : 'stat-daily-avg-visits'
    },
    {
      title: 'Toplam Cirom',
      value: `₺${(data.my_revenue || 0).toLocaleString('tr-TR')}`,
      icon: DollarSign,
      gradient: 'from-emerald-500 to-emerald-600',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      testId: 'stat-my-revenue'
    },
    {
      title: 'Toplam Puanım',
      value: `₺${(data.my_profit || 0).toLocaleString('tr-TR')}`,
      icon: Award,
      gradient: 'from-green-500 to-green-600',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      testId: 'stat-my-profit'
    },
  ];

  const allStatCards = [...statCards, ...myPerformanceCards];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Başlık ve Filtreler - KOMPAKT */}
      <div className="mb-4">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg shadow-md p-2 sm:p-3 mb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h1 className="text-lg sm:text-xl font-bold">Dashboard</h1>
              <p className="text-xs text-white/90 flex items-center gap-1">
                <Users className="w-3 h-3" />
                Hoş geldiniz, <span className="font-semibold">{user?.full_name}</span>
              </p>
            </div>
          </div>
        </div>
        
        {/* Filters - KOMPAKT */}
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
          <div className="flex flex-wrap items-center gap-2">
            {/* Hızlı Filtre Butonları */}
            <Button
              onClick={() => {
                const today = new Date();
                const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
                const formatDate = (date) => {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  return `${year}-${month}-${day}`;
                };
                setStartDate(formatDate(lastMonth));
                setEndDate(formatDate(lastMonthEnd));
              }}
              variant="outline"
              size="sm"
              className="text-xs h-8 px-2"
            >
              Geçen Ay
            </Button>
            <Button
              onClick={() => {
                const today = new Date();
                const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                const formatDate = (date) => {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  return `${year}-${month}-${day}`;
                };
                setStartDate(formatDate(firstDay));
                setEndDate(formatDate(today));
              }}
              variant="outline"
              size="sm"
              className="text-xs h-8 px-2"
            >
              Bu Ay
            </Button>
            
            {/* Tarih Seçiciler */}
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-gray-400 text-xs">—</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            
            {/* Temsilci Filtresi */}
            {user?.role === 'admin' && (
              <Select value={selectedRepFilter} onValueChange={setSelectedRepFilter}>
                <SelectTrigger className="w-[160px] h-8 text-xs">
                  <SelectValue placeholder="Tüm Kullanıcılar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Kullanıcılar</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.username}>{u.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      {/* 3 Aydır Temas Edilmeyen Müşteriler */}
      {(noContactCustomers.length > 0 || noContactTotalCount > 0) && (
        <div className="bg-white border border-amber-200 rounded-xl shadow-sm overflow-hidden" data-testid="no-contact-alert">
          <div className="p-4 md:p-6 border-b bg-amber-50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  3 Aydır Temas Edilmeyen Müşteriler
                </h3>
                <p className="text-sm text-gray-500">
                  Son 90 günde ziyaret, arama veya satış kaydı olmayan müşteriler
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={exportNoContactToExcel}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="export-no-contact-excel-inline"
              >
                <Download className="w-4 h-4 mr-1" />
                Excel'e Aktar
              </Button>
              <div className="bg-amber-100 text-amber-800 px-4 py-2 rounded-lg text-lg font-bold">
                {noContactTotalCount > 0 ? noContactTotalCount : noContactCustomers.length} müşteri
              </div>
              {(noContactTotalCount > 10 || noContactCustomers.length > 10) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowNoContactDialog(true)}
                  className="text-amber-700 border-amber-300"
                >
                  Tümünü Gör
                </Button>
              )}
            </div>
          </div>
          
          {/* Temsilci bazlı özet (sadece admin) */}
          {user?.role === 'admin' && (() => {
            // Yeni API'den gelen rep_counts varsa onu kullan, yoksa eski hesaplama
            const repCounts = Object.keys(noContactRepCounts).length > 0 
              ? noContactRepCounts 
              : noContactCustomers.reduce((acc, customer) => {
                  const rep = customer.representative || 'Atanmamış';
                  acc[rep] = (acc[rep] || 0) + 1;
                  return acc;
                }, {});
            
            return (
              <div className="px-4 py-3 bg-amber-50/50 border-b flex flex-wrap gap-2">
                {Object.entries(repCounts)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 8)
                  .map(([rep, count]) => (
                    <span key={rep} className="bg-white text-gray-700 px-3 py-1 rounded-full text-sm border">
                      {rep}: <strong>{count}</strong>
                    </span>
                  ))}
              </div>
            );
          })()}
          
          <div className="overflow-x-auto max-h-[350px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Müşteri</th>
                  {user?.role === 'admin' && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Temsilci</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Telefon</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Araç</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Son Temas</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Geçen Süre</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {noContactCustomers.slice(0, 10).map((customer, idx) => (
                  <tr 
                    key={idx} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => customer.customer_id && navigate(`/customers/${customer.customer_id}`)}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{customer.customer_name}</p>
                    </td>
                    {user?.role === 'admin' && (
                      <td className="px-4 py-3 text-sm text-gray-600">{customer.representative || 'Atanmamış'}</td>
                    )}
                    <td className="px-4 py-3">
                      {customer.phone ? (
                        <a 
                          href={`tel:${customer.phone}`} 
                          className="text-sm text-green-600 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {customer.phone}
                        </a>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 rounded text-xs font-bold bg-blue-100 text-blue-700">
                        {customer.device_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm">
                      {customer.last_contact_date 
                        ? (
                          <div>
                            <span>{format(new Date(customer.last_contact_date), 'dd MMM yyyy', { locale: tr })}</span>
                            {customer.last_contact_type && (
                              <span className="block text-xs text-gray-400">{customer.last_contact_type}</span>
                            )}
                          </div>
                        )
                        : <span className="text-gray-400">Hiç temas yok</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-center">
                      {customer.days_since_contact !== null ? (
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          customer.days_since_contact > 180 
                            ? 'bg-red-100 text-red-700' 
                            : customer.days_since_contact > 120
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-amber-100 text-amber-700'
                        }`}>
                          {customer.days_since_contact} gün
                        </span>
                      ) : (
                        <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">
                          ∞
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 3 Aydır Temas Edilmeyen Müşteriler Dialog */}
      <Dialog open={showNoContactDialog} onOpenChange={setShowNoContactDialog}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                3 Aydır Temas Edilmeyen Müşteriler ({noContactTotalCount > 0 ? noContactTotalCount : noContactCustomers.length} Müşteri)
                {noContactCustomers.length < noContactTotalCount && (
                  <span className="text-sm font-normal text-gray-500">
                    (ilk {noContactCustomers.length} gösteriliyor)
                  </span>
                )}
              </div>
              <Button
                onClick={exportNoContactToExcel}
                className="bg-green-600 hover:bg-green-700 text-white text-sm"
                data-testid="export-no-contact-excel"
              >
                <Download className="w-4 h-4 mr-2" />
                Excel'e Aktar
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto mt-4">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Müşteri</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Temsilci</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Telefon</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Araç</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Son Temas</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Temas Türü</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Geçen Gün</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {noContactCustomers.map((customer, idx) => (
                  <tr 
                    key={idx} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      setShowNoContactDialog(false);
                      customer.customer_id && navigate(`/customers/${customer.customer_id}`);
                    }}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{customer.customer_name}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{customer.representative || 'Atanmamış'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{customer.phone || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 rounded text-xs font-bold bg-blue-100 text-blue-700">
                        {customer.device_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm">
                      {customer.last_contact_date 
                        ? format(new Date(customer.last_contact_date), 'dd MMM yyyy', { locale: tr })
                        : <span className="text-gray-400">Hiç temas yok</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-center">
                      {customer.last_contact_type ? (
                        <span className="bg-slate-100 text-slate-700 px-2 py-1 rounded text-xs">
                          {customer.last_contact_type}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {customer.days_since_contact !== null ? (
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          customer.days_since_contact > 180 
                            ? 'bg-red-100 text-red-700' 
                            : customer.days_since_contact > 120
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-amber-100 text-amber-700'
                        }`}>
                          {customer.days_since_contact} gün
                        </span>
                      ) : (
                        <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">
                          ∞
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modern Stat Cards */}
      {user?.role !== 'admin' ? (
        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {allStatCards.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div
                    key={index}
                    className="group relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-5 border border-gray-100 hover:border-gray-200 overflow-hidden"
                    data-testid={stat.testId}
                  >
                    {/* Gradient Background */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                    
                    <div className="relative z-10">
                      {/* Icon */}
                      <div className={`${stat.iconBg} w-12 h-12 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className={`w-6 h-6 ${stat.iconColor}`} />
                      </div>
                      
                      {/* Content */}
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{stat.title}</p>
                      <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                      {stat.subtitle && (
                        <p className="text-xs text-gray-500 mt-2 line-clamp-2">{stat.subtitle}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="lg:col-span-1">
            <TargetAchievementCard targetAchievement={targetAchievement} showDetailLink={false} />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {allStatCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="group relative bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-4 border border-gray-100 hover:border-gray-200 overflow-hidden"
                data-testid={stat.testId}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                
                <div className="relative z-10">
                  <div className={`${stat.iconBg} w-10 h-10 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-5 h-5 ${stat.iconColor}`} />
                  </div>
                  
                  <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide mb-1">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  {stat.subtitle && (
                    <p className="text-[10px] text-gray-500 mt-1 line-clamp-2">{stat.subtitle}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <TeamRanking currentUsername={user?.username} />
        <PrimHistoryChart isAdmin={user?.role === 'admin'} />
      </div>

      {/* Rest of the code remains the same... */}
      {/* Kategori Bazlı Satış Raporu */}
      {data && data.category_sales && Object.keys(data.category_sales).length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
          <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-4">Kategori Bazlı Satış Raporu</h2>
          <div className="overflow-x-auto">
            <table className="w-full table-auto" style={{ minWidth: '400px' }}>
              <thead className="progo-gradient text-gray-800">
                <tr>
                  <th className="px-3 md:px-4 py-3 text-left whitespace-nowrap text-sm md:text-base">Kategori</th>
                  <th className="px-3 md:px-4 py-3 text-center whitespace-nowrap text-sm md:text-base">Satış Adedi</th>
                  <th className="px-3 md:px-4 py-3 text-right whitespace-nowrap text-sm md:text-base">Toplam Ciro</th>
                  <th className="px-3 md:px-4 py-3 text-right whitespace-nowrap text-sm md:text-base">Toplam Puan</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(data.category_sales)
                  .sort((a, b) => (data.category_sales[b] || 0) - (data.category_sales[a] || 0))
                  .map((category) => (
                  <tr key={category} className="border-b hover:bg-gray-50">
                    <td className="px-3 md:px-4 py-3 font-medium text-sm md:text-base">{category}</td>
                    <td className="px-3 md:px-4 py-3 text-center">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs md:text-sm font-medium">
                        {data.category_sales[category] || 0}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 py-3 text-right text-sm md:text-base">
                      {(data.category_revenue?.[category] || 0).toLocaleString('tr-TR')} ₺
                    </td>
                    <td className="px-3 md:px-4 py-3 text-right text-sm md:text-base">
                      {(data.category_profit?.[category] || 0).toLocaleString('tr-TR')} ₺
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-100 font-bold">
                  <td className="px-3 md:px-4 py-3 text-sm md:text-base">TOPLAM</td>
                  <td className="px-3 md:px-4 py-3 text-center">
                    <span className="bg-blue-200 text-blue-900 px-3 py-1 rounded-full text-xs md:text-sm font-bold">
                      {Object.values(data.category_sales).reduce((sum, val) => sum + val, 0)}
                    </span>
                  </td>
                  <td className="px-3 md:px-4 py-3 text-right text-sm md:text-base">
                    {Object.values(data.category_revenue || {}).reduce((sum, val) => sum + val, 0).toLocaleString('tr-TR')} ₺
                  </td>
                  <td className="px-3 md:px-4 py-3 text-right text-sm md:text-base">
                    {Object.values(data.category_profit || {}).reduce((sum, val) => sum + val, 0).toLocaleString('tr-TR')} ₺
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Temsilci Performans Tablosu */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-800">Temsilci Performans Tablosu</h3>
          {user?.role === 'admin' && (
            <Button 
              onClick={exportRepPerformanceToExcel}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Dışarı Aktar
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full table-auto" style={{ minWidth: '600px' }}>
            <thead className="progo-gradient text-gray-800">
              <tr>
                <th className="px-4 py-3 text-left whitespace-nowrap">Temsilci</th>
                <th className="px-4 py-3 text-center whitespace-nowrap">
                  {user?.role === 'abonelik' ? 'Arama Sayısı' : 'Ziyaret Sayısı'}
                </th>
                <th className="px-4 py-3 text-center whitespace-nowrap">Satılan Arvento</th>
                <th className="px-4 py-3 text-center whitespace-nowrap">Satılan Kamera</th>
                <th className="px-4 py-3 text-center whitespace-nowrap">Satılan Sensör</th>
                <th className="px-4 py-3 text-center whitespace-nowrap">Satılan M2M</th>
                <th className="px-4 py-3 text-center whitespace-nowrap">Toplam Puan</th>
                <th className="px-4 py-3 text-center whitespace-nowrap">Hedef Gerçekleştirme</th>
              </tr>
            </thead>
            <tbody>
              {!hasRepData ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    Henüz veri yok
                  </td>
                </tr>
              ) : (
                (() => {
                  const now = new Date();
                  const currentMonth = now.getMonth() + 1;
                  const currentYear = now.getFullYear();
                  
                  const filteredUsers = users.filter(u => u.role === 'temsilci' || u.role === 'abonelik');
                  
                  const repsWithAchievement = filteredUsers.map(u => {
                      const rep = u.username;
                      const achievement = data.rep_target_achievement?.[rep];
                      return { rep, achievement: achievement !== null && achievement !== undefined ? achievement : null };
                    });
                  
                  const sortedReps = repsWithAchievement.sort((a, b) => {
                    const aVal = a.achievement !== null ? parseFloat(a.achievement) : -1;
                    const bVal = b.achievement !== null ? parseFloat(b.achievement) : -1;
                    return bVal - aVal;
                  });
                  
                  return sortedReps.map(({ rep, achievement }, index) => {
                    const getRowBgColor = () => {
                      if (achievement === null || achievement === undefined) return '';
                      const rate = parseFloat(achievement);
                      if (rate >= 180) return 'bg-green-50 hover:bg-green-100';
                      if (rate >= 120) return 'bg-emerald-50 hover:bg-emerald-100';
                      if (rate >= 100) return 'bg-yellow-50 hover:bg-yellow-100';
                      return 'bg-red-50 hover:bg-red-100';
                    };
                    
                    return (
                    <tr key={rep} className={`border-b ${getRowBgColor()}`} data-testid={`rep-row-${index}`}>
                      <td className="px-4 py-3 font-medium text-sm">{rep}</td>
                      <td 
                        className="px-4 py-3 text-center cursor-pointer hover:bg-blue-50 transition-colors"
                        onClick={() => handleRepPerformanceClick(rep, user?.role === 'abonelik' ? 'calls' : 'visits')}
                      >
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                          {user?.role === 'abonelik' ? (repData.calls?.[rep] || 0) : (repData.visits[rep] || 0)}
                        </span>
                      </td>
                      <td 
                        className="px-4 py-3 text-center cursor-pointer hover:bg-green-50 transition-colors"
                        onClick={() => handleRepPerformanceClick(rep, 'arvento')}
                      >
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                          {repData.sales[rep] || 0}
                        </span>
                      </td>
                      <td 
                        className="px-4 py-3 text-center cursor-pointer hover:bg-purple-50 transition-colors"
                        onClick={() => handleRepPerformanceClick(rep, 'camera')}
                      >
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">
                          {repData.cameras[rep] || 0}
                        </span>
                      </td>
                      <td 
                        className="px-4 py-3 text-center cursor-pointer hover:bg-yellow-50 transition-colors"
                        onClick={() => handleRepPerformanceClick(rep, 'sensors')}
                      >
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                          {repData.sensors?.[rep] || 0}
                        </span>
                      </td>
                      <td 
                        className="px-4 py-3 text-center cursor-pointer hover:bg-indigo-50 transition-colors"
                        onClick={() => handleRepPerformanceClick(rep, 'm2m')}
                      >
                        <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-xs font-medium">
                          {repData.m2m?.[rep] || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center font-semibold text-gray-800 whitespace-nowrap" data-testid={`rep-points-${rep}`}>
                        {fmtPoints(repData.points[rep])}
                      </td>
                      <td className="px-4 py-3" data-testid={`rep-achievement-${rep}`}>
                        <AchievementBar rate={achievement} />
                      </td>
                    </tr>
                    );
                  });
                })()
              )}
              {hasRepData ? (
                <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                  <td className="px-4 py-3 text-sm">TOPLAM</td>
                  <td className="px-4 py-3 text-center">
                    <span className="bg-blue-200 text-blue-900 px-3 py-1 rounded-full text-sm font-bold">
                      {user?.role === 'abonelik' 
                        ? Object.values(repData.calls || {}).reduce((sum, val) => sum + val, 0)
                        : Object.values(repData.visits).reduce((sum, val) => sum + val, 0)
                      }
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="bg-green-200 text-green-900 px-3 py-1 rounded-full text-sm font-bold">
                      {Object.values(repData.sales).reduce((sum, val) => sum + val, 0)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="bg-purple-200 text-purple-900 px-3 py-1 rounded-full text-sm font-bold">
                      {Object.values(repData.cameras).reduce((sum, val) => sum + val, 0)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="bg-yellow-200 text-yellow-900 px-3 py-1 rounded-full text-sm font-bold">
                      {Object.values(repData.sensors || {}).reduce((sum, val) => sum + val, 0)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="bg-indigo-200 text-indigo-900 px-3 py-1 rounded-full text-sm font-bold">
                      {Object.values(repData.m2m || {}).reduce((sum, val) => sum + val, 0)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center whitespace-nowrap" data-testid="rep-points-total">
                    {fmtPoints(users.filter(u => u.role === 'temsilci' || u.role === 'abonelik').reduce((s, u) => s + (repData.points[u.username] || 0), 0))}
                  </td>
                  <td className="px-4 py-3"></td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* Son Sistem Girişleri */}
      {user?.role === 'admin' && data.recent_logins && data.recent_logins.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Son Sistem Girişleri</h3>
          <div className="overflow-x-auto">
            <table className="w-full table-auto" style={{ minWidth: '600px' }}>
              <thead className="border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Kullanıcı</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Rol</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Son Giriş</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_logins.map((login, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium">{login.full_name}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        login.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {login.role === 'admin' ? 'Admin' : 'Temsilci'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {login.last_login ? format(new Date(login.last_login), 'dd MMM yyyy HH:mm', { locale: tr }) : 'Hiç giriş yapmadı'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Representative Performance Details Dialog */}
      <Dialog open={showRepDetailsDialog} onOpenChange={setShowRepDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {repDetailsData.rep} - {
                repDetailsData.type === 'visits' ? 'Ziyaretler' :
                repDetailsData.type === 'arvento' ? 'Cihaz Satışları' :
                repDetailsData.type === 'camera' ? 'Kamera Satışları' :
                repDetailsData.type === 'sensors' ? 'Sensör Satışları' :
                repDetailsData.type === 'm2m' ? 'M2M Satışları' : ''
              }
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {repDetailsData.data.length === 0 ? (
              <p className="text-center py-8 text-gray-500">Veri bulunamadı</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Müşteri</th>
                      {repDetailsData.type !== 'visits' && repDetailsData.type !== 'calls' && (
                        <>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Ürün</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Adet</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Birim Fiyat</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Toplam Fiyat</th>
                        </>
                      )}
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Tarih</th>
                      {(repDetailsData.type === 'visits' || repDetailsData.type === 'calls') && (
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Notlar</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {repDetailsData.data.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{item.customer}</td>
                        {repDetailsData.type !== 'visits' && repDetailsData.type !== 'calls' && (
                          <>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.product}</td>
                            <td className="px-4 py-3 text-sm text-center text-gray-900">{item.quantity}</td>
                            <td className="px-4 py-3 text-sm text-right text-gray-900">
                              {item.unit_price?.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ₺
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-900">
                              {item.price?.toLocaleString('tr-TR')} ₺
                            </td>
                          </>
                        )}
                        <td className="px-4 py-3 text-sm text-center text-gray-600">
                          {new Date(item.date).toLocaleDateString('tr-TR')}
                        </td>
                        {(repDetailsData.type === 'visits' || repDetailsData.type === 'calls') && (
                          <td className="px-4 py-3 text-sm text-gray-600">{item.notes || '-'}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  {repDetailsData.type !== 'visits' && repDetailsData.type !== 'calls' && (
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">Toplam</td>
                        <td></td>
                        <td className="px-4 py-3 text-sm text-center font-semibold text-gray-900">
                          {repDetailsData.data.reduce((sum, item) => sum + item.quantity, 0)} Adet
                        </td>
                        <td></td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                          {repDetailsData.data.reduce((sum, item) => sum + item.price, 0).toLocaleString('tr-TR')} ₺
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
